import { useState } from 'react'

import styled from 'styled-components'
import Link from 'next/link'
import { animated, config, useTrail } from '@react-spring/web'
import { getDisplayNameFromGobblerAuthAccount } from 'utils/auth'
import LoadingSpinner from 'components/core/LoadingSpinner'
import MiddleTimer from 'components/mint/gobbler/MiddleTimer'
import NoGobblersView, { NoPagesView } from 'components/factory/NoGobblersView'

import { useSmartContractReadCall } from 'hooks/react-query-typechain/useSmartContractReadCall'
import useOwnedGobblers, { GobblerInfo } from 'hooks/gobblers/useOwnedGobblers'
import useContracts from 'hooks/mint/useContracts'
import { useIsMounted } from 'hooks/util/useIsMounted'

import { DEFAULT_CHAIN_ID } from 'config'
import { GlaminatedPageInfo, OwnedPageInfo, usePagesOwned } from 'hooks/pages/usePagesOwned'
import { cdnRoot } from 'utils/cdn'
import { DEFAULT_ARTWORK_TITLE } from 'components/draw/constants'

enum SelectorState {
  gobblers,
  pages,
}
function Header({
  selected,
  setSelected,
}: {
  selected: SelectorState
  setSelected: React.Dispatch<React.SetStateAction<SelectorState>>
}) {
  return (
    <SelectorContainer>
      <Selector
        onClick={() => setSelected(SelectorState.gobblers)}
        selected={selected === SelectorState.gobblers}
      >
        Gobblers
      </Selector>
      <Selector
        onClick={() => setSelected(SelectorState.pages)}
        selected={selected === SelectorState.pages}
      >
        Pages
      </Selector>
    </SelectorContainer>
  )
}

function UnrevealedGobblerDiv() {
  const [gobblerContract] = useContracts(DEFAULT_CHAIN_ID)
  const gobblersRevealDataQuery = useSmartContractReadCall(gobblerContract, 'gobblerRevealsData')
  const revealData = gobblersRevealDataQuery.data
  const timeStamp = revealData?.[1]?.toNumber()
  return (
    <Link passHref href={{ pathname: '/gobbler/mint' }}>
      <GobblerViewDiv>
        {timeStamp && <MiddleTimer timeStamp={timeStamp} />}
        <GobblerImage src={`${cdnRoot}/images/dashboard/cooking_card.png`} />
      </GobblerViewDiv>
    </Link>
  )
}

function GobblerView({ gobbler }: { gobbler: GobblerInfo }) {
  const { revealed, image, gobblerTokenId } = gobbler

  if (!revealed) {
    return <UnrevealedGobblerDiv />
  }

  const href = { pathname: '/gobbler/[gobblerId]', query: { gobblerId: gobblerTokenId } }

  return (
    <Link href={href} passHref>
      <GobblerViewDiv>
        <GobblerImage src={image} />
      </GobblerViewDiv>
    </Link>
  )
}

function GobblersView({ gobblers, isLoading }: { gobblers: GobblerInfo[]; isLoading: boolean }) {
  console.log('x:gobblers', gobblers)
  if (isLoading) {
    return (
      <GobblersViewDiv>
        <div
          style={{ display: 'flex', marginTop: 24, justifyContent: 'center', alignItems: 'center' }}
        >
          <LoadingSpinner />
        </div>
      </GobblersViewDiv>
    )
  }

  if (!gobblers.length) {
    return (
      <NoGobblersView
        panelTitle="No Gobblers"
        containerStyle={{ margin: 0, marginTop: 24, width: '100%' }}
      />
    )
  }

  return (
    <GobblersViewDiv>
      {gobblers.map((gobbler) => (
        <GobblerView key={gobbler.gobblerTokenId} gobbler={gobbler} />
      ))}
    </GobblersViewDiv>
  )
}

function PageView(props: OwnedPageInfo | { id: string }) {
  const { id } = props

  // @ts-ignore
  if ((props as OwnedPageInfo).art_drawing_short_id) {
    // @ts-ignore
    const { artwork_title, cdn_image_url, drawing } = props as GlaminatedPageInfo
    const { avatar_img_src, twitter_image } = drawing.author

    const authorName = getDisplayNameFromGobblerAuthAccount(drawing.author, true)

    return (
      <Link
        passHref
        href={{
          pathname: '/page/[pageId]',
          query: { pageId: id },
        }}
      >
        <PageViewDiv>
          <PageImage src={cdn_image_url} />
          <PageInfo>
            <div>{artwork_title || DEFAULT_ARTWORK_TITLE}</div>
            <div>
              <ProfileSmallImage
                src={avatar_img_src ?? twitter_image ?? `${cdnRoot}/images/gobbler-avatar.png`}
              />
              Drawn by {authorName}
            </div>
          </PageInfo>
        </PageViewDiv>
      </Link>
    )
  }
  return (
    <Link
      passHref
      href={{
        pathname: '/page/[pageId]',
        query: { pageId: id },
      }}
    >
      <PageViewDiv>
        <PageImage src={`${cdnRoot}/images/dashboard/blank.gif`} />
        <PageInfo>
          <div>Art Gobbler Page #{id}</div>
          <div>Blank</div>
        </PageInfo>
      </PageViewDiv>
    </Link>
  )
}
function PagesView({
  isLoading,
  pages,
}: {
  isLoading: boolean
  pages: undefined | (OwnedPageInfo | { id: string })[]
}) {
  if (isLoading) {
    return (
      <GobblersViewDiv>
        <div
          style={{ display: 'flex', marginTop: 24, justifyContent: 'center', alignItems: 'center' }}
        >
          <LoadingSpinner />
        </div>
      </GobblersViewDiv>
    )
  }
  if (!pages || !pages.length) {
    return <NoPagesView />
  }

  return (
    <GobblersViewDiv>
      {pages.map((page) => (
        <>
          <PageView key={page.id} {...page} />
        </>
      ))}
    </GobblersViewDiv>
  )
}

function DashboardGobblersAndPages() {
  const [selected, setSelected] = useState<SelectorState>(SelectorState.gobblers)

  const [gobblers, loadingGobblers] = useOwnedGobblers(true)
  const [pages, loadingPages] = usePagesOwned()

  console.log('y:gobblers', gobblers)

  // console.log('gobblers', gobblers)
  return (
    <Container>
      <Header selected={selected} setSelected={setSelected} />
      {selected === SelectorState.gobblers && (
        <GobblersView gobblers={gobblers} isLoading={loadingGobblers} />
      )}
      {selected === SelectorState.pages && (
        <PagesView isLoading={loadingPages as boolean} pages={pages} />
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 36px;
  width: 100%;
`

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
`

const Selector = styled.div<{
  selected?: boolean
}>`
  font-size: 28px;
  font-weight: 700;
  border-radius: 100px;
  padding: 16px 24px;
  ${(props) =>
    props.selected &&
    `
    background: #000000;
    box-shadow: 0px 16px 32px rgba(0, 0, 0, 0.06), 0px 12px 16px rgba(0, 0, 0, 0.04),
    0px 4px 4px rgba(0, 0, 0, 0.03), inset 0px 4px 4px rgba(255, 255, 255, 0.25);
  `}

  transition: transform 0.2s ease-in-out;
  &:hover {
    cursor: pointer;
    transform: scale(0.975);
  }
`

const RawGobblersViewDiv = styled.div`
  margin-top: 24px;
  min-height: 532px;
  display: flex;
  flex-direction: row;
  gap: 24px;
  flex-wrap: wrap;
`

const GobblersViewDiv = animated(RawGobblersViewDiv)

const GobblerViewDiv = styled.a`
  width: 396px;
  height: 396px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
    height: auto;
    aspect-ratio: 1/1;
  `};

  display: flex;
  justify-content: center;
  align-items: center;
  border: 4px solid #000000;

  box-shadow: 0px -1.51406px 4.0375px rgba(255, 255, 255, 0.2),
    0px 12.1125px 20.1875px rgba(0, 0, 0, 0.08), 0px 6.05625px 10.0938px rgba(0, 0, 0, 0.06),
    0px 2.01875px 4.0375px rgba(0, 0, 0, 0.04);
  border-radius: 32px;

  position: relative;
  overflow: hidden;

  transition: transform 0.2s ease-in-out;
  text-decoration: none;
  &:hover {
    cursor: pointer;
    transform: scale(0.975);
  }
`

const GobblerImage = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
  position: absolute;
`

// PageView

const PageViewDiv = styled.a`
  width: 390px;
  height: 501px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
  width: 100%;
  height: auto;
`};

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 4px solid #000000;

  box-shadow: 0px -1.51406px 4.0375px rgba(255, 255, 255, 0.2),
    0px 12.1125px 20.1875px rgba(0, 0, 0, 0.08), 0px 6.05625px 10.0938px rgba(0, 0, 0, 0.06),
    0px 2.01875px 4.0375px rgba(0, 0, 0, 0.04);
  border-radius: 32px;

  position: relative;
  overflow: hidden;

  transition: transform 0.2s ease-in-out;
  text-decoration: none;
  &:hover {
    cursor: pointer;
    transform: scale(0.975);
  }
`

const PageImage = styled.img`
  width: 100%;
  aspect-ratio: 1/1;
  background: white;
`

const ProfileSmallImage = styled.img`
  height: 32px;
  width: 32px;
  border: 1px solid #ffffff;
  border-radius: 50%;
`

const PageInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  flex-grow: 1;

  padding-bottom: 10px;
  background: #26262b;
  padding-left: 24px;
  width: 100%;
  border-top: 4px solid #080a0c;
  min-height: 100px;

  > div:first-child {
    font-size: 28px;
    font-weight: 700;
  }

  > div:nth-child(2) {
    font-size: 18px;
    font-weight: 600;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
`

export default DashboardGobblersAndPages
